import React from "react";
import { Clock, Send, Building2, Newspaper } from "lucide-react";

import PageLayout from "@/components/layout/PageLayout";
import Seo from "@/components/foundation/Seo";
import Container from "@/components/foundation/Container";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import Badge from "@/components/foundation/Badge";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import GridPattern from "@/components/foundation/GridPattern";

import ContactForm from "@/components/marketing/ContactForm";

const INFO_ITEMS = [
  {
    icon: Clock,
    title: "Response time",
    body: "We respond to inquiries within 1–2 business days.",
  },
  {
    icon: Send,
    title: "General inquiries",
    body: "Use the form to reach our team.",
  },
  {
    icon: Building2,
    title: "Funding partners",
    body: 'Choose "Funding Partner Inquiry" in the reason dropdown for expedited routing.',
  },
  {
    icon: Newspaper,
    title: "Press",
    body: 'Choose "Press" in the reason dropdown for media requests.',
  },
];

export default function Contact() {
  return (
    <PageLayout>
      <Seo
        title="ZenMe Capital · Contact"
        description="Get in touch with ZenMe Capital. Whether you're a business, a funding partner, press, or need support — we'd love to hear from you."
        path="/contact"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-28 pb-12 sm:pt-32 sm:pb-16"
        data-testid="contact-hero"
      >
        <GridPattern variant="dots" size={28} opacity={0.4} />
        <GlowOrb color="emerald" size={460} intensity={0.18} className="-bottom-40 -left-40" />

        <Container className="relative z-10">
          <div className="max-w-3xl">
            <FadeIn>
              <Badge variant="emerald" pulse>Contact</Badge>
            </FadeIn>
            <FadeIn delay={0.06}>
              <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Let&rsquo;s talk.
              </h1>
            </FadeIn>
            <FadeIn delay={0.14}>
              <p className="mt-6 max-w-xl text-base text-text-secondary sm:text-lg leading-relaxed">
                Whether you&rsquo;re a business looking for funding, a funding
                partner, or a member of the press — we&rsquo;d love to hear
                from you.
              </p>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* FORM + HOW WE HANDLE INQUIRIES */}
      <Section spacing="md">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <FadeIn>
              <Card variant="glass" interactive={false} className="p-8 sm:p-10">
                <ContactForm source="contact_page" />
              </Card>
            </FadeIn>
          </div>
          <div className="lg:col-span-4">
            <FadeIn delay={0.1}>
              <Card
                variant="elevated"
                interactive={false}
                className="flex h-full flex-col gap-5 p-8"
                data-testid="contact-info-card"
              >
                <div className="flex flex-col gap-2">
                  <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-emerald">
                    How we handle inquiries
                  </span>
                  <h3 className="font-display text-2xl font-semibold text-white">
                    Every message reaches the right team.
                  </h3>
                </div>

                <ul className="mt-2 flex flex-col gap-4">
                  {INFO_ITEMS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li
                        key={item.title}
                        className="flex items-start gap-3"
                        data-testid={`contact-info-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-brand-emerald/25 bg-brand-emerald/8 text-brand-emerald">
                          <Icon className="h-4 w-4" strokeWidth={1.75} />
                        </span>
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-white">
                            {item.title}
                          </span>
                          <span className="mt-0.5 text-xs leading-relaxed text-text-secondary">
                            {item.body}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="mt-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                  <p className="text-xs leading-relaxed text-text-muted">
                    Support requests are triaged based on urgency. Sensitive or
                    security-related reports are prioritized.
                  </p>
                </div>
              </Card>
            </FadeIn>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
}
